import React, { useState, createContext, useContext } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations
const translations = {
  en: {
    // Navigation
    home: "Home",
    accommodations: "Accommodations",
    gallery: "Gallery",
    usefulInformation: "Useful Information",
    reviews: "Reviews",
    about: "About",
    localArea: "Local Area",
    contact: "Contact",
    mobileMenu: "Toggle navigation menu",

    // Header info
    headerLocation: "Beautiful Crete, Greece",
    guesthouse: "Guesthouse",

    // About Page - Hero Section
    aboutHeroTitle: "Welcome to Crete",
    aboutHeroSubtitle: "An island of beautiful contrasts, where high mountains meet ancient culture and timeless traditions come alive",

    // About Page - Full Story Text (as written)
    aboutStoryTitle: "About Agape Community Guesthouse",
    storyParagraph1: "Welcome to Crete, an island of beautiful contrasts, where high mountains meet ancient olive groves covering dry mountains. This is a place with thousands of years of history and a rich culture you can feel everywhere—from ancient ruins to the warm and friendly hospitality of its people.",
    storyParagraph2: "You will find Agape Community Guesthouse in the heart of this magic, in the sleepy village of Ano Viannos. The village is located 66km southeast of Heraklion, resting 550 meters up on the slopes of the legendary Dikti mountain, where the ancient Greek god Zeus was born. This is a place that has kept its authentic, traditional charm, and it is filled with myths. Even its name, Viannos, comes from a myth about the Kourites—powerful half-gods who protected the baby Zeus from his father Kronos by clashing their swords and shields to hide his cries.",
    storyParagraph3: "From the village, you can look out over a sea of olive trees and see the Libyan Sea through a natural opening in the mountains. The countryside around us is serene and beautiful, with olive, plane, and poplar trees, and towering mountain gorges, where eagles and vultures circle above their hidden nests. The village itself is a joy to explore, with its narrow, winding streets, traditional stone houses, cool fountains, and small, shady squares where you can relax in a local cafe.",
    storyParagraph4: "Just 50 meters away from the guesthouse is the heart of the village is the central square, where we invite you to sit under the ancient plane tree, just as the locals do. From here, you can easily explore the area's treasures. Discover the beautifully restored watermill, visit the Viannos Folklore Museum to learn about our local life, or explore the dozens of historic churches and the old Agia Moni monastery.",
    storyParagraph5: "For nature lovers, many hiking trails begin right from the village. You can explore impressive canyons like Portela Gorge, famous for its waterfalls, or hike the Mesonas Gorge Trail to find a perfect mix of nature and ancient ruins. The region also offers more challenging routes for experienced hikers and canyoners.",
    storyParagraph6: "In Ano Viannos, you will find everything you need for a perfect stay - away from the touristic hotspots of the island. Enjoy the authentic Crete village life with charming coffee shops and delicious tavernas. You will also find practical things like ATMs, bus stops, and a medical center.",
    storyParagraph7: "Our guesthouse is an extension of the Agape Community, a vibrant project focused on sustainable living, spiritual growth, and community connection. We invite you to experience our way of life by joining our regular yoga and meditation sessions, or by participating in one of our many retreats and workshops. This is a place to not only discover the beauty of Crete, but also to connect with like-minded people and with yourself. You can learn more about our community's vision and find a calendar of events ",
    storyParagraph7Link: "here",
    storyParagraph7LinkUrl: "https://agapecrete.com/program/",

    // About Page - Features Grid
    aboutFeaturesTitle: "Why Choose {guesthouseName}?",
    aboutFeaturesSubtitle: "Experience authentic Crete village life away from touristic hotspots",
    featurePrimeLocationTitle: "Village Heart",
    featurePrimeLocationDescription: "Just 50 meters from the central square under the ancient plane tree, in the heart of traditional Ano Viannos",
    featureFamilyOperatedTitle: "Agape Community",
    featureFamilyOperatedDescription: "Part of a vibrant community focused on sustainable living, spiritual growth, and meaningful connections",
    featureExcellentReviewsTitle: "Nature & Culture",
    featureExcellentReviewsDescription: "Hiking trails, ancient monasteries, folklore museums, and rich cultural heritage",
    featurePersonalTouchTitle: "Authentic Experience",
    featurePersonalTouchDescription: "Join yoga, meditation sessions, and workshops while exploring the real Crete",

    // About Page - Location & Access
    aboutLocationTitle: "Village Life & Natural Beauty",
    aboutLocationParagraph1: "The village itself is a joy to explore, with its narrow, winding streets, traditional stone houses, cool fountains, and small, shady squares where you can relax in a local cafe. Discover the beautifully restored watermill, visit the Viannos Folklore Museum to learn about our local life, or explore the dozens of historic churches and the old Agia Moni monastery.",
    aboutLocationParagraph2: "For nature lovers, many hiking trails begin right from the village. You can explore impressive canyons like Portela Gorge, famous for its waterfalls, or hike the Mesonas Gorge Trail to find a perfect mix of nature and ancient ruins.",
    locationNatureAccess: "Mountain trails and natural wonders",
    locationChaniaOldTown: "Portela Gorge with waterfalls",
    locationAirport: "66km southeast of Heraklion",
    locationRestaurants: "Traditional tavernas in the village",

    // About Page - Contact CTA
    aboutCtaTitle: "Ready for Your Authentic Crete Experience?",
    aboutCtaSubtitle: "Come discover the beauty of Crete and connect with like-minded people in our community",
    aboutCtaCallUs: "Call Us:",
    aboutCtaEmailUs: "Email Us",

    // Local Area Page
    localAreaHeroTitle: "Explore Crete",
    localAreaHeroSubtitle: "Discover amazing attractions, authentic dining, and unforgettable experiences near our guesthouse",
    localAreaMapTitle: "Explore the Area", 
    localAreaMapSubtitle: "Discover attractions around Ano Viannos on our interactive map. Click on any marker to learn more and get directions.",
    
    // Category filters
    categoryAll: "All Attractions",
    categoryBeaches: "Beaches",
    categoryDining: "Dining",
    categoryHistorical: "Historical Sites",
    categoryNature: "Nature & Hiking",
    
    // Map
    mapLegend: "Legend",
    
    localAreaAllAttractions: "All Attractions",
    localAreaBeaches: "Beaches",
    localAreaDining: "Dining", 
    localAreaHistoricalSites: "Historical Sites",
    localAreaNatureHiking: "Nature & Hiking",
    localAreaErrorTitle: "Error Loading Attractions",
    localAreaNoAttractionsTitle: "No attractions found",
    localAreaNoAttractionsText: "Try selecting a different category or check back later for new additions.",
    localAreaTipsTitle: "Local Insider Tips",
    localAreaTipsSubtitle: "Make the most of your Cretan adventure with our local recommendations",
    localAreaBestTimesTitle: "Best Times to Visit",
    localAreaBestTimesText: "Early morning (7-9 AM) or late afternoon (5-7 PM) for hiking and sightseeing. Restaurants are busiest 8-10 PM - book ahead!",
    localAreaHiddenGemsTitle: "Hidden Gems", 
    localAreaHiddenGemsText: "Ask us about hidden trails, family-run tavernas, and local festivals. We love sharing Crete's best-kept secrets with our guests!",
    localAreaGettingAroundTitle: "Getting Around",
    localAreaGettingAroundText: "We provide maps and can help arrange transportation. Many attractions are walkable or a short bus ride away.",

    // Useful Information
    usefulInfoTitle: "Useful Information",
    usefulInfoSubtitle: "Important contacts and emergency numbers for your stay in Crete",
    usefulInfoEmergencyServices: "Emergency Services",
    usefulInfoLocalServices: "Local Services",
    usefulInfoYourAccommodation: "Your Accommodation",
    usefulInfoImportantNotes: "Important Notes",
    usefulInfoNote1: "For medical emergencies, call 166 (ambulance) or go directly to Regional Clinic Ano Viannos",
    usefulInfoNote2: "The European emergency number 112 works from any phone, even without signal bars",
    usefulInfoNote3: "Tourist Police (171) speak multiple languages and can assist with travel-related issues",
    usefulInfoNote4: "Contact us anytime for local assistance, recommendations, or emergency support",

    // Useful Information - Contact Names and Descriptions
    usefulInfoPolice: "Police",
    usefulInfoPoliceDesc: "Emergency police services",
    usefulInfoMedicalEmergency: "Medical Emergency Calling Center",
    usefulInfoMedicalEmergencyDesc: "Medical emergency calling center",
    usefulInfoFireDept: "Fire Department",
    usefulInfoFireDeptDesc: "Fire emergency and rescue",
    usefulInfoForestFire: "Fire Department for Forest Fires",
    usefulInfoForestFireDesc: "Fire department for forest fires",
    usefulInfoCoastGuard: "Coast Guard",
    usefulInfoCoastGuardDesc: "Coast guard emergency services",
    usefulInfoEuropeanEmergency: "European Emergency Number",
    usefulInfoEuropeanEmergencyDesc: "European emergency number",
    usefulInfoTouristPolice: "Tourist Police",
    usefulInfoTouristPoliceDesc: "Service in English, French, German and Greek",
    usefulInfoRegionalHealth: "Regional Health Center Ano Viannos",
    usefulInfoRegionalHealthDesc: "Regional health center ano viannos",
    usefulInfoPoliceViannos: "Police Department Viannos",
    usefulInfoPoliceViannosDesc: "Police department viannos",
    usefulInfoMunicipality: "Viannos Municipality",
    usefulInfoMunicipalityDesc: "Viannos municipality",
    usefulInfoTouristOrg: "Greek National Tourist Organization Crete HQ",
    usefulInfoTouristOrgDesc: "Greek national tourist organization crete HQ",
    usefulInfoGuesthouse: "Agape Guesthouse",
    usefulInfoGuesthouseDesc: "Your accommodation - we're here to help!",
    usefulInfoGuesthouseAvailable: "24/7 for emergencies",
    usefulInfoHours247: "24/7",
    usefulInfoHoursWeekdays: "Mon-Fri: 8:00-15:00",

    // Contact Page
    contactHeroTitle: "Contact Us",
    contactHeroSubtitle: "Ready to book your perfect Cretan getaway? We're here to help make it happen",
    contactGetInTouch: "Get in Touch",
    contactDescription: "We're excited to welcome you to {guesthouse}! Reach out to us for bookings, questions, or to learn more about our beautiful island.",
    contactPhone: "Phone",
    contactEmail: "Email",
    contactAddress: "Address",
    contactCheckInOut: "Check-in / Check-out",
    bookingInquiryTitle: "Book Your Stay",
    bookingInquiryMessage: "For booking inquiries, please contact us directly via phone or email. We look forward to welcoming you to our guesthouse!",
    contactCheckInOutValue: "{checkIn} / Check-out by 11:00 AM",
    
    // Booking Form
    bookingFormTitle: "Book Your Stay",
    bookingFormSubtitle: "Fill out the form below and we'll get back to you within 24 hours with availability and pricing information.",
    bookingAccommodation: "Select Accommodation (Optional)",
    bookingAccommodationAny: "Any available accommodation",
    bookingAccommodationPrice: "€{price}/night (up to {capacity} guests)",
    bookingLoadingAvailability: "Loading availability...",
    bookingUnavailableDates: "⚠️ Some dates may be unavailable for this accommodation. Unavailable periods will be highlighted in the date selection.",
    bookingFullName: "Full Name",
    bookingFullNamePlaceholder: "Enter your full name",
    bookingEmail: "Email Address",
    bookingEmailPlaceholder: "Enter your email",
    bookingEmailInvalid: "Please enter a valid email address",
    bookingPhone: "Phone Number",
    bookingPhonePlaceholder: "Enter your phone number",
    bookingDates: "Check-in & Check-out Dates",
    bookingDatesPlaceholder: "Select your stay dates",
    bookingDatesPlaceholderNoAccommodation: "Select an accommodation first",
    bookingGuests: "Number of Guests",
    bookingMessage: "Special Requests or Questions",
    bookingMessagePlaceholder: "Let us know if you have any special requests, dietary requirements, or questions about your stay...",
    bookingSubmit: "Send Booking Request",
    bookingSubmitting: "Submitting Request...",
    bookingValidationWarning: "⚠️ Please complete all required fields:",
    bookingValidationName: " Name",
    bookingValidationEmail: " • Valid Email",
    bookingValidationCheckin: " • Check-in Date",
    bookingValidationCheckout: " • Check-out Date",
    bookingValidationDateRange: " • Valid Date Range",
    bookingValidationGuests: " • Number of Guests",
    bookingSuccessMessage: "✅ Thank you! Your booking request has been submitted successfully. We'll contact you within 24 hours with availability and pricing information.",
    bookingErrorMessage: "❌ There was an error submitting your request. Please try again or contact us directly.",
    bookingPrivacyNote: "By submitting this form, you agree to be contacted regarding your booking request.",
    bookOnline: "Book Online",
    bookingComingSoon: "Bookings Coming Soon",
    
    // Map Section
    contactMapTitle: "Find Us",
    contactMapSubtitle: "Located in the heart of beautiful Crete, in the authentic village of Ano Viannos",
    contactMapButton: "Open in Google Maps",

    // Footer
    quickLinks: "Quick Links",
    experienceCrete: "Experience Crete",
    experienceDescription: "Discover the authentic beauty of Crete at our charming guesthouse. From ancient history to mountain adventures, your perfect Mediterranean getaway awaits.",
    support247: "24/7 Support",
    freeWifi: "Free WiFi",
    footerCopyright: "Experience authentic Cretan hospitality",
    footerAllRightsReserved: "All rights reserved.",
    footerWebsiteEstablished: "Website established September 2025",

    // Footer links
    ruralDevelopment: "Rural Development Program",
    agricultureDirectorate: "Agriculture and Rural Development Directorate of the European Commission",

    // Amenities
    amenities: "Amenities",
    localAmenities: "Local Amenities",
    localAmenitiesSubtitle: "Discover nearby facilities and services for your convenience",
    contentComingSoon: "Content Coming Soon",
    gatheringInformation: "We're currently gathering comprehensive information about local amenities and services.",
    pendingContent: "Pending content from 3rd party providers - check back soon for detailed amenity listings!"
  },
  gr: {
    // Navigation
    home: "Αρχική",
    accommodations: "Καταλύματα",
    gallery: "Γκαλερί",
    usefulInformation: "Χρήσιμες Πληροφορίες",
    reviews: "Κριτικές",
    about: "Σχετικά",
    localArea: "Περιοχή",
    contact: "Επικοινωνία",
    mobileMenu: "Εναλλαγή μενού πλοήγησης",

    // Header info
    headerLocation: "Όμορφη Κρήτη, Ελλάδα",
    guesthouse: "Ξενώνας",

    // About Page - Hero Section
    aboutHeroTitle: "Σχετικά με εμάς",
    aboutHeroSubtitle: "Ανακαλύψτε την ιστορία πίσω από τον αυθεντικό κρητικό ξενώνα μας",

    // About Page - Main Story Section
    aboutStoryTitle: "Σχετικά με τον Ξενώνα Agape Community",
    aboutStoryParagraph1: "Καλώς ήρθατε στο {guesthouseName}, όπου τρεις γενιές κρητικής φιλοξενίας ενώνονται για να δημιουργήσουν αξέχαστες εμπειρίες για τους επισκέπτες μας. Ο οικογενειακός μας ξενώνας καλωσορίζει επισκέπτες σε αυτή την όμορφη γωνιά της Κρήτης από το 1985.",
    aboutStoryParagraph2: "Αυτό που ξεκίνησε ως ένα μικρό οικογενειακό σπίτι έχει εξελιχθεί σε έναν γοητευτικό ξενώνα που συνδυάζει τέλεια την παραδοσιακή κρητική αρχιτεκτονική με τη σύγχρονη άνεση. Κάθε γωνιά αφηγείται μια ιστορία, και κάθε επισκέπτης γίνεται μέρος της διευρυμένης οικογένειάς μας.",
    aboutStoryParagraph3: "Βρισκόμαστε σε μια από τις πιο γραφικές περιοχές της Κρήτης, και είμαστε παθιασμένοι με το να μοιραζόμαστε την αυθεντική ομορφιά του νησιού μας - από κρυφές παραλίες και αρχαίους ελαιώνες μέχρι τοπικές ταβέρνες που σερβίρουν συνταγές που έχουν περάσει από γενιά σε γενιά.",

    // Story paragraphs (matching English keys)
    storyParagraph1: "Καλώς ήρθατε στην Κρήτη, ένα νησί με όμορφες αντιθέσεις, όπου ψηλά βουνά συναντούν παρθένες παραλίες και αρχαίοι ελαιώνες καλύπτουν ξηρά βουνά. Αυτό είναι ένα μέρος με χιλιάδες χρόνια ιστορίας και πλούσιο πολιτισμό που μπορείτε να νιώσετε παντού—από αρχαία ερείπια μέχρι τη ζεστή και φιλική φιλοξενία των ανθρώπων του.",
    storyParagraph2: "Θα βρείτε τον Ξενώνα Agape Community στην καρδιά αυτής της μαγείας, στο ήσυχο χωριό του Άνω Βιάννου. Το χωριό βρίσκεται 66 χιλιόμετρα νοτιοανατολικά του Ηρακλείου, στα 550 μέτρα ύψος στις πλαγιές του θρυλικού βουνού Δίκτη, όπου γεννήθηκε ο αρχαίος ελληνικός θεός Δίας. Αυτό είναι ένα μέρος που έχει διατηρήσει την αυθεντική, παραδοσιακή του γοητεία και είναι γεμάτο μύθους. Ακόμη και το όνομά του, Βιάννος, προέρχεται από έναν μύθο για τους Κουρήτες—ισχυρούς ημίθεους που προστάτευαν το μωρό Δία από τον πατέρα του Κρόνο χτυπώντας τα σπαθιά και τις ασπίδες τους για να κρύψουν τις κραυγές του.",
    storyParagraph3: "Από το χωριό, μπορείτε να κοιτάξετε πάνω από μια θάλασσα ελαιόδεντρων και να δείτε τη Λιβυκή Θάλασσα μέσα από ένα φυσικό άνοιγμα στα βουνά. Η ύπαιθρος γύρω μας είναι γαλήνια και όμορφη, με ελιές, πλατάνια και λεύκες, και επιβλητικά ορεινά φαράγγια, όπου αετοί και όρνια κυκλώνουν πάνω από τις κρυφές φωλιές τους. Το ίδιο το χωριό είναι μια χαρά να εξερευνήσετε, με τα στενά, περιστροφικά δρομάκια του, τα παραδοσιακά πέτρινα σπίτια, τις δροσερές βρύσες και τις μικρές, σκιερές πλατείες όπου μπορείτε να χαλαρώσετε σε ένα τοπικό καφενείο.",
    storyParagraph4: "Μόλις 50 μέτρα μακριά από τον ξενώνα είναι η καρδιά του χωριού, η κεντρική πλατεία, όπου σας προσκαλούμε να καθίσετε κάτω από τον αρχαίο πλάτανο, όπως κάνουν οι ντόπιοι. Από εδώ, μπορείτε εύκολα να εξερευνήσετε τους θησαυρούς της περιοχής. Ανακαλύψτε τον όμορφα αποκατεστημένο νερόμυλο, επισκεφθείτε το Λαογραφικό Μουσείο Βιάννου για να μάθετε για την τοπική μας ζωή, ή εξερευνήστε τις δεκάδες ιστορικές εκκλησίες και την παλιά μονή Αγία Μονή.",
    storyParagraph5: "Για τους λάτρεις της φύσης, πολλά μονοπάτια πεζοπορίας ξεκινούν ακριβώς από το χωριό. Μπορείτε να εξερευνήσετε εντυπωσιακά φαράγγια όπως το Φαράγγι της Πορτέλας, διάσημο για τους καταρράκτες του, ή να πεζοπορήσετε στο μονοπάτι του Φαραγγιού Μεσονάς για να βρείτε έναν τέλειο συνδυασμό φύσης και αρχαίων ερειπίων. Η περιοχή προσφέρει επίσης πιο απαιτητικές διαδρομές για έμπειρους πεζοπόρους και canyoners.",
    storyParagraph6: "Στον Άνω Βιάννο, θα βρείτε όλα όσα χρειάζεστε για μια τέλεια διαμονή - μακριά από τα τουριστικά hotspots του νησιού. Απολαύστε την αυθεντική ζωή του κρητικού χωριού αλλά με υπέροχες θέες, γοητευτικά καφενεία και νόστιμες ταβέρνες. Θα βρείτε επίσης πρακτικά πράγματα όπως ΑΤΜ, στάσεις λεωφορείων και ιατρικό κέντρο.",
    storyParagraph7: "Ο ξενώνας μας είναι μια επέκταση της Κοινότητας Agape, ένα ζωντανό έργο που επικεντρώνεται στη βιώσιμη ζωή, την πνευματική ανάπτυξη και τη σύνδεση της κοινότητας. Σας προσκαλούμε να ζήσετε τον τρόπο ζωής μας συμμετέχοντας στις τακτικές συνεδρίες γιόγκα και διαλογισμού μας, ή συμμετέχοντας σε ένα από τα πολλά retreats και workshops μας. Αυτό είναι ένα μέρος όχι μόνο για να ανακαλύψετε την ομορφιά της Κρήτης, αλλά και για να συνδεθείτε με ομοϊδεάτες ανθρώπους και με τον εαυτό σας. Μπορείτε να μάθετε περισσότερα για το όραμα της κοινότητάς μας και να βρείτε ένα ημερολόγιο εκδηλώσεων ",
    storyParagraph7Link: "εδώ",
    storyParagraph7LinkUrl: "https://agapecrete.com/program/",

    // About Page - Features Grid
    aboutFeaturesTitle: "Γιατί να επιλέξετε το {guesthouseName};",
    aboutFeaturesSubtitle: "Ζήστε τον τέλειο συνδυασμό παραδοσιακής γοητείας και σύγχρονης φιλοξενίας",
    featurePrimeLocationTitle: "Κορυφαία Τοποθεσία",
    featurePrimeLocationDescription: "Βρίσκεται στην καρδιά της Κρήτης, μόλις 5 λεπτά από παρθένες παραλίες και αυθεντικές ταβέρνες",
    featureFamilyOperatedTitle: "Οικογενειακή Επιχείρηση",
    featureFamilyOperatedDescription: "Τρεις γενιές κρητικής φιλοξενίας, προσφέροντας αυθεντικές τοπικές εμπειρίες",
    featureExcellentReviewsTitle: "Εξαιρετικές Κριτικές",
    featureExcellentReviewsDescription: "Συνεχώς αξιολογείται ως ένας από τους κορυφαίους ξενώνες στην περιοχή",
    featurePersonalTouchTitle: "Προσωπική Επαφή",
    featurePersonalTouchDescription: "Κάθε επισκέπτης αντιμετωπίζεται σαν οικογένεια, με εξατομικευμένες συστάσεις και φροντίδα",

    // About Page - Location & Access
    aboutLocationTitle: "Ιδανική Τοποθεσία",
    aboutLocationParagraph1: "Το {guesthouseName} βρίσκεται σε στρατηγική θέση για να σας προσφέρει τα καλύτερα και των δύο κόσμων - γαλήνια ηρεμία και εύκολη πρόσβαση στα πιο όμορφα αξιοθέατα της Κρήτης.",
    aboutLocationParagraph2: "Είμαστε μόλις 5 λεπτά με τα πόδια από μερικές από τις πιο παρθένες παραλίες του νησιού, όπου τα κρυστάλλινα νερά συναντούν τη χρυσή άμμο. Η γοητευτική παλιά πόλη με το ενετικό λιμάνι της, τις παραδοσιακές αγορές και τις αυθεντικές ταβέρνες είναι εύκολα προσβάσιμη.",
    locationNatureAccess: "Μονοπάτια βουνού και φυσικά θαύματα",
    locationChaniaOldTown: "10 λεπτά από την Παλιά Πόλη των Χανίων",
    locationAirport: "15 λεπτά από το αεροδρόμιο",
    locationRestaurants: "Σε κοντινή απόσταση με τα πόδια από εστιατόρια",

    // About Page - Contact CTA
    aboutCtaTitle: "Έτοιμοι να ζήσετε την Κρήτη;",
    aboutCtaSubtitle: "Αφήστε την οικογένειά μας να μοιραστεί την αυθεντική ομορφία της Κρήτης μαζί σας",
    aboutCtaCallUs: "Καλέστε μας:",
    aboutCtaEmailUs: "Στείλτε μας email",

    // Local Area Page
    localAreaHeroTitle: "Εξερευνήστε την Κρήτη",
    localAreaHeroSubtitle: "Ανακαλύψτε καταπληκτικά αξιοθέατα, αυθεντική κουζίνα και αξέχαστες εμπειρίες κοντά στον ξενώνα μας",
    localAreaMapTitle: "Εξερευνήστε την Περιοχή",
    localAreaMapSubtitle: "Ανακαλύψτε αξιοθέατα γύρω από τον Άνω Βιάννο στον διαδραστικό μας χάρτη. Κάντε κλικ σε οποιοδήποτε σημείο για περισσότερες πληροφορίες.",
    
    // Category filters
    categoryAll: "Όλα τα Αξιοθέατα",
    categoryBeaches: "Παραλίες",
    categoryDining: "Εστιατόρια",
    categoryHistorical: "Ιστορικά Μνημεία",
    categoryNature: "Φύση & Πεζοπορία",
    
    // Map
    mapLegend: "Υπόμνημα",
    
    localAreaAllAttractions: "Όλα τα Αξιοθέατα",
    localAreaBeaches: "Παραλίες",
    localAreaDining: "Εστιατόρια",
    localAreaHistoricalSites: "Ιστορικά Μνημεία",
    localAreaNatureHiking: "Φύση & Πεζοπορία",
    localAreaErrorTitle: "Σφάλμα Φόρτωσης Αξιοθέατων",
    localAreaNoAttractionsTitle: "Δεν βρέθηκαν αξιοθέατα",
    localAreaNoAttractionsText: "Δοκιμάστε να επιλέξετε διαφορετική κατηγορία ή ελέγξτε ξανά αργότερα.",
    localAreaTipsTitle: "Τοπικές Συμβουλές",
    localAreaTipsSubtitle: "Αξιοποιήστε στο μέγιστο την κρητική σας περιπέτεια με τις τοπικές μας συστάσεις",
    localAreaBestTimesTitle: "Καλύτερες Ώρες Επίσκεψης",
    localAreaBestTimesText: "Πρωί νωρίς (7-9 π.μ.) ή αργά το απόγευμα (5-7 μ.μ.) για παραλίες. Τα εστιατόρια είναι πιο πολυσύχναστα 8-10 μ.μ. - κλείστε εκ των προτέρων!",
    localAreaHiddenGemsTitle: "Κρυφοί Θησαυροί",
    localAreaHiddenGemsText: "Ρωτήστε μας για μυστικές παραλίες, οικογενειακές ταβέρνες και τοπικά φεστιβάλ. Μας αρέσει να μοιραζόμαστε τα καλύτερα μυστικά της Κρήτης!",
    localAreaGettingAroundTitle: "Μετακίνηση",
    localAreaGettingAroundText: "Παρέχουμε χάρτες και μπορούμε να βοηθήσουμε με τη μεταφορά. Πολλά αξιοθέατα είναι προσβάσιμα με τα πόδια ή με σύντομη διαδρομή με λεωφορείο.",

    // Useful Information
    usefulInfoTitle: "Χρήσιμες Πληροφορίες",
    usefulInfoSubtitle: "Σημαντικές επαφές και αριθμοί έκτακτης ανάγκης για τη διαμονή σας στην Κρήτη",
    usefulInfoEmergencyServices: "Υπηρεσίες Έκτακτης Ανάγκης",
    usefulInfoLocalServices: "Τοπικές Υπηρεσίες",
    usefulInfoYourAccommodation: "Το Κατάλυμά σας",
    usefulInfoImportantNotes: "Σημαντικές Σημειώσεις",
    usefulInfoNote1: "Για ιατρικές έκτακτες ανάγκες, καλέστε το 166 (ασθενοφόρο) ή πηγαίνετε απευθείας στην Περιφερειακή Κλινική Άνω Βιάννου",
    usefulInfoNote2: "Ο ευρωπαϊκός αριθμός έκτακτης ανάγκης 112 λειτουργεί από οποιοδήποτε τηλέφωνο, ακόμη και χωρίς σήμα",
    usefulInfoNote3: "Η Τουριστική Αστυνομία (171) μιλάει πολλές γλώσσες και μπορεί να βοηθήσει με θέματα ταξιδιού",
    usefulInfoNote4: "Επικοινωνήστε μαζί μας οποτεδήποτε για τοπική βοήθεια, συστάσεις ή υποστήριξη έκτακτης ανάγκης",

    // Useful Information - Contact Names and Descriptions
    usefulInfoPolice: "Αστυνομία",
    usefulInfoPoliceDesc: "Υπηρεσίες έκτακτης ανάγκης αστυνομίας",
    usefulInfoMedicalEmergency: "Κέντρο Κλήσεων Ιατρικής Έκτακτης Ανάγκης",
    usefulInfoMedicalEmergencyDesc: "Κέντρο κλήσεων ιατρικής έκτακτης ανάγκης",
    usefulInfoFireDept: "Πυροσβεστική Υπηρεσία",
    usefulInfoFireDeptDesc: "Έκτακτη ανάγκη πυρκαγιάς και διάσωσης",
    usefulInfoForestFire: "Πυροσβεστική Υπηρεσία για Δασικές Πυρκαγιές",
    usefulInfoForestFireDesc: "Πυροσβεστική υπηρεσία για δασικές πυρκαγιές",
    usefulInfoCoastGuard: "Λιμενικό Σώμα",
    usefulInfoCoastGuardDesc: "Υπηρεσίες έκτακτης ανάγκης λιμενικού σώματος",
    usefulInfoEuropeanEmergency: "Ευρωπαϊκός Αριθμός Έκτακτης Ανάγκης",
    usefulInfoEuropeanEmergencyDesc: "Ευρωπαϊκός αριθμός έκτακτης ανάγκης",
    usefulInfoTouristPolice: "Τουριστική Αστυνομία",
    usefulInfoTouristPoliceDesc: "Υπηρεσία στα Αγγλικά, Γαλλικά, Γερμανικά και Ελληνικά",
    usefulInfoRegionalHealth: "Περιφερειακό Κέντρο Υγείας Άνω Βιάννου",
    usefulInfoRegionalHealthDesc: "Περιφερειακό κέντρο υγείας άνω βιάννου",
    usefulInfoPoliceViannos: "Αστυνομικό Τμήμα Βιάννου",
    usefulInfoPoliceViannosDesc: "Αστυνομικό τμήμα βιάννου",
    usefulInfoMunicipality: "Δήμος Βιάννου",
    usefulInfoMunicipalityDesc: "Δήμος βιάννου",
    usefulInfoTouristOrg: "Ελληνικός Οργανισμός Τουρισμού Κρήτης",
    usefulInfoTouristOrgDesc: "Ελληνικός οργανισμός τουρισμού κρήτης",
    usefulInfoGuesthouse: "Ξενώνας Agape",
    usefulInfoGuesthouseDesc: "Το κατάλυμά σας - είμαστε εδώ για να βοηθήσουμε!",
    usefulInfoGuesthouseAvailable: "24/7 για έκτακτες ανάγκες",
    usefulInfoHours247: "24/7",
    usefulInfoHoursWeekdays: "Δευ-Παρ: 8:00-15:00",

    // Contact Page
    contactHeroTitle: "Επικοινωνήστε μαζί μας",
    contactHeroSubtitle: "Έτοιμοι να κλείσετε την τέλεια κρητική σας απόδραση; Είμαστε εδώ για να σας βοηθήσουμε να την πραγματοποιήσετε",
    contactGetInTouch: "Επικοινωνήστε μαζί μας",
    contactDescription: "Είμαστε ενθουσιασμένοι να σας καλωσορίσουμε στο {guesthouse}! Επικοινωνήστε μαζί μας για κρατήσεις, ερωτήσεις ή για να μάθετε περισσότερα για το όμορφο νησί μας.",
    contactPhone: "Τηλέφωνο",
    contactEmail: "Email",
    contactAddress: "Διεύθυνση",
    contactCheckInOut: "Check-in / Check-out",
    bookingInquiryTitle: "Κλείστε τη Διαμονή σας",
    bookingInquiryMessage: "Για ερωτήσεις κρατήσεων, παρακαλούμε επικοινωνήστε μαζί μας απευθείας μέσω τηλεφώνου ή email. Ανυπομονούμε να σας καλωσορίσουμε στο ξενώνα μας!",
    contactCheckInOutValue: "{checkIn} / Check-out έως τις 11:00 π.μ.",
    
    // Booking Form
    bookingFormTitle: "Κλείστε τη Διαμονή σας",
    bookingFormSubtitle: "Συμπληρώστε την παρακάτω φόρμα και θα επικοινωνήσουμε μαζί σας εντός 24 ωρών με πληροφορίες διαθεσιμότητας και τιμών.",
    bookingAccommodation: "Επιλέξτε Κατάλυμα (Προαιρετικό)",
    bookingAccommodationAny: "Οποιοδήποτε διαθέσιμο κατάλυμα",
    bookingAccommodationPrice: "€{price}/βράδυ (έως {capacity} επισκέπτες)",
    bookingLoadingAvailability: "Φόρτωση διαθεσιμότητας...",
    bookingUnavailableDates: "⚠️ Ορισμένες ημερομηνίες μπορεί να μην είναι διαθέσιμες για αυτό το κατάλυμα. Οι μη διαθέσιμες περίοδοι θα επισημανθούν στην επιλογή ημερομηνιών.",
    bookingFullName: "Πλήρες Όνομα",
    bookingFullNamePlaceholder: "Εισάγετε το πλήρες όνομά σας",
    bookingEmail: "Διεύθυνση Email",
    bookingEmailPlaceholder: "Εισάγετε το email σας",
    bookingEmailInvalid: "Παρακαλώ εισάγετε μια έγκυρη διεύθυνση email",
    bookingPhone: "Αριθμός Τηλεφώνου",
    bookingPhonePlaceholder: "Εισάγετε τον αριθμό τηλεφώνου σας",
    bookingDates: "Ημερομηνίες Check-in & Check-out",
    bookingDatesPlaceholder: "Επιλέξτε τις ημερομηνίες διαμονής σας",
    bookingDatesPlaceholderNoAccommodation: "Επιλέξτε πρώτα ένα κατάλυμα",
    bookingGuests: "Αριθμός Επισκεπτών",
    bookingMessage: "Ειδικά Αιτήματα ή Ερωτήσεις",
    bookingMessagePlaceholder: "Ενημερώστε μας αν έχετε ειδικά αιτήματα, διατροφικές απαιτήσεις ή ερωτήσεις για τη διαμονή σας...",
    bookingSubmit: "Αποστολή Αιτήματος Κράτησης",
    bookingSubmitting: "Αποστολή Αιτήματος...",
    bookingValidationWarning: "⚠️ Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία:",
    bookingValidationName: " Όνομα",
    bookingValidationEmail: " • Έγκυρο Email",
    bookingValidationCheckin: " • Ημερομηνία Check-in",
    bookingValidationCheckout: " • Ημερομηνία Check-out",
    bookingValidationDateRange: " • Έγκυρο Εύρος Ημερομηνιών",
    bookingValidationGuests: " • Αριθμός Επισκεπτών",
    bookingSuccessMessage: "✅ Ευχαριστούμε! Το αίτημα κράτησής σας υποβλήθηκε επιτυχώς. Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών με πληροφορίες διαθεσιμότητας και τιμών.",
    bookingErrorMessage: "❌ Υπήρξε σφάλμα κατά την υποβολή του αιτήματός σας. Παρακαλώ δοκιμάστε ξανά ή επικοινωνήστε μαζί μας απευθείας.",
    bookingPrivacyNote: "Υποβάλλοντας αυτή τη φόρμα, συμφωνείτε να επικοινωνήσουμε μαζί σας σχετικά με το αίτημα κράτησής σας.",
    bookOnline: "Κράτηση Online",
    bookingComingSoon: "Κρατήσεις Σύντομα Διαθέσιμες",
    
    // Map Section
    contactMapTitle: "Βρείτε μας",
    contactMapSubtitle: "Βρισκόμαστε στην καρδιά της όμορφης Κρήτης, στο αυθεντικό χωριό του Άνω Βιάννου",
    contactMapButton: "Άνοιγμα στο Google Maps",

    // Footer
    quickLinks: "Γρήγοροι Σύνδεσμοι",
    experienceCrete: "Ζήστε την Κρήτη",
    experienceDescription: "Ανακαλύψτε την αυθεντική ομορφιά της Κρήτης στον όμορφο ξενώνα μας. Από παρθένες παραλίες μέχρι αρχαία ιστορία, η τέλεια μεσογειακή απόδρασή σας σας περιμένει.",
    support247: "24/7 Υποστήριξη",
    freeWifi: "Δωρεάν WiFi",
    footerCopyright: "Ζήστε την αυθεντική κρητική φιλοξενία",
    footerAllRightsReserved: "Όλα τα δικαιώματα διατηρούνται.",
    footerWebsiteEstablished: "Ιστοσελίδα δημιουργήθηκε Σεπτέμβριος 2025",

    // Footer links
    ruralDevelopment: "Προγράμματος Αγροτικής Ανάπτυξης",
    agricultureDirectorate: "Διεύθυνσης Γεωργίας και Αγροτικής Ανάπτυξης της Ευρωπαϊκής Επιτροπής",

    // Amenities
    amenities: "Παροχές",
    localAmenities: "Τοπικές Παροχές",
    localAmenitiesSubtitle: "Ανακαλύψτε κοντινές εγκαταστάσεις και υπηρεσίες για τη διευκόλυνσή σας",
    contentComingSoon: "Περιεχόμενο Σύντομα",
    gatheringInformation: "Συλλέγουμε αυτή τη στιγμή ολοκληρωμένες πληροφορίες για τοπικές παροχές και υπηρεσίες.",
    pendingContent: "Εκκρεμεί περιεχόμενο από τρίτους παρόχους - ελέγξτε ξανά σύντομα για λεπτομερείς λίστες παροχών!"
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key, variables = {}) => {
    let translatedString = translations[language][key] || key;
    for (const [varName, varValue] of Object.entries(variables)) {
      translatedString = translatedString.replace(new RegExp(`{${varName}}`, 'g'), varValue);
    }
    return translatedString;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
